import { ReactNode, useCallback, useMemo, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { getNextPageParam } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

const SEARCH_DEBOUNCE_MS = 200;
/** Short-list entities change rarely and are invalidated by their own mutations. */
const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;
const GC_TIME_MS = 10 * 60 * 1000;
const SKELETON_ROW_COUNT = 3;

export interface PaginatedCommandDialogProps<T extends { id: number }> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchPlaceholder: string;
  /** TanStack Query key prefix; the debounced search term is appended. Pages are cached per term. */
  queryKey: string[];
  /** Loads one page of candidates for `search`, starting at `cursor`. */
  fetchItems: (search: string, cursor: number | null) => Promise<PaginatedResponse<T>>;
  /** How long fetched pages stay fresh. Default: 5 minutes. */
  staleTime?: number;
  onSelect: (item: T) => void;
  renderItem: (item: T) => ReactNode;
  /** Value cmdk uses for keyboard navigation / de-duplication. */
  getItemLabel: (item: T) => string;
  /** Ids hidden from the list (e.g. already picked). Applied in render, never in the query. */
  excludeIds?: Set<number>;
  /**
   * A short reason why an item cannot be picked. Such items stay listed, highlighted with a
   * warning, but are not selectable. `null` means selectable.
   */
  getItemWarning?: (item: T) => string | null;
  /** Shown when the search returned nothing. */
  emptyMessage: string;
  /** Shown when the search returned items but `excludeIds` removed all of them. */
  allExcludedMessage?: string;
}

/**
 * Searchable command dialog backed by a cursor-paginated endpoint: debounced server search,
 * pages cached per search term via `useInfiniteQuery`, infinite scroll with a bottom loader,
 * skeleton on first load. Data loading mirrors `SingleSelectWithPagination`; use this when the
 * picker is a standalone dialog rather than a form field.
 */
export function PaginatedCommandDialog<T extends { id: number }>({
  open,
  onOpenChange,
  searchPlaceholder,
  queryKey,
  fetchItems,
  staleTime = DEFAULT_STALE_TIME_MS,
  onSelect,
  renderItem,
  getItemLabel,
  excludeIds,
  getItemWarning,
  emptyMessage,
  allExcludedMessage,
}: PaginatedCommandDialogProps<T>) {
  const { t } = useTranslation('common');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: [...queryKey, debouncedSearch],
    enabled: open,
    queryFn: ({ pageParam }) => fetchItems(debouncedSearch, pageParam),
    getNextPageParam,
    initialPageParam: null as number | null,
    staleTime,
    gcTime: GC_TIME_MS,
  });

  const { setContainerElement, setSentinelElement } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetching: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  const fetched = useMemo(() => data?.pages.flatMap(page => page.items) ?? [], [data]);
  const results = useMemo(
    () => (excludeIds ? fetched.filter(item => !excludeIds.has(item.id)) : fetched),
    [fetched, excludeIds]
  );
  const isEverythingExcluded = fetched.length > 0 && results.length === 0;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setSearchTerm('');
      onOpenChange(nextOpen);
    },
    [onOpenChange]
  );

  const handleSelect = useCallback(
    (item: T) => {
      setSearchTerm('');
      onOpenChange(false);
      onSelect(item);
    },
    [onOpenChange, onSelect]
  );

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange}>
      <CommandInput
        placeholder={searchPlaceholder}
        value={searchTerm}
        onValueChange={setSearchTerm}
      />
      <CommandList ref={setContainerElement}>
        {isLoading ? (
          <div className="flex flex-col gap-2 p-3" aria-busy="true">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <>
            <CommandEmpty className="px-6 py-6 text-center text-sm text-muted-foreground">
              {isEverythingExcluded ? (allExcludedMessage ?? emptyMessage) : emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {results.map(item => {
                const warning = getItemWarning?.(item) ?? null;
                return (
                  <CommandItem
                    key={item.id}
                    value={getItemLabel(item)}
                    disabled={warning !== null}
                    onSelect={() => handleSelect(item)}
                    className="justify-between gap-3"
                  >
                    {renderItem(item)}
                    {warning && (
                      <span className="flex shrink-0 items-center gap-1.5 text-xs text-warning">
                        <TriangleAlertIcon size={14} className="shrink-0" />
                        {warning}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {isFetchingNextPage && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('loadingMore')}
              </div>
            )}
            {hasNextPage && <div ref={setSentinelElement} className="h-1" />}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
